// src/app/shared/helpers/date.helper.ts

export class DateHelper {
  /**
   * Convert Gregorian (Miladi) date to Hijri date
   * @param gregorianDate Date object or string (YYYY-MM-DD)
   * @returns string in format DD/MM/YYYY
   */
  static gregorianToHijri(gregorianDate: Date | string): string {
    try {
      const date = typeof gregorianDate === 'string'
        ? new Date(gregorianDate)
        : gregorianDate;

      const formatter = new Intl.DateTimeFormat('ar-SA-u-ca-islamic', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      return formatter.format(date);
    } catch (error) {
      console.error('Error converting to Hijri:', error);
      return '';
    }
  }

  /**
   * Convert Hijri date to Gregorian (Miladi) date
   * @param hijriDate string in format DD/MM/YYYY
   * @returns Date object
   */
  static hijriToGregorian(hijriDate: string): Date {
    try {
      const [day, month, year] = hijriDate.split('/').map(Number);

      const formatter = new Intl.DateTimeFormat('en-u-ca-islamic', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      });

      let approxGregorian = new Date(year + 580, month - 1, day);

      while (formatter.format(approxGregorian) !== hijriDate) {
        approxGregorian.setDate(approxGregorian.getDate() + 1);
      }

      return approxGregorian;
    } catch (error) {
      console.error('Error converting to Gregorian:', error);
      return new Date();
    }
  }

  /**
   * Format Gregorian date to string
   * @param date Date object
   * @param format desired format (default: DD/MM/YYYY)
   * @returns formatted date string
   */
  static formatGregorianDate(date: Date, format: 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY' = 'DD/MM/YYYY'): string {
    try {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      switch (format) {
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MM/DD/YYYY':
          return `${month}/${day}/${year}`;
        default:
          return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Convert between Hijri and Gregorian dates with formatting options
   */
  static convertDate(
    date: string | Date,
    toCalendar: 'hijri' | 'gregorian',
    format: 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY' = 'DD/MM/YYYY'
  ): string {
    try {
      if (toCalendar === 'hijri') {
        return this.gregorianToHijri(date);
      } else {
        return this.formatGregorianDate(this.hijriToGregorian(date as string), format);
      }
    } catch (error) {
      console.error(`Error converting date to ${toCalendar}:`, error);
      return '';
    }
  }
}
